import { motion } from 'framer-motion'
import { CheckCircle, AlertTriangle, XCircle, Activity, Brain, Shield, Eye, BookOpen, Moon, Leaf, Scale, Atom } from 'lucide-react'
import SiteHeader from '../components/SiteHeader'
import SiteFooter from '../components/SiteFooter'

const bodyFont = {
  fontFamily: '"Helvetica Neue", sans-serif',
  fontWeight: 300,
} as const

const brandFont = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", -apple-system, sans-serif',
  fontWeight: 100,
} as const

const CONSTELLATION_APIS = [
  { icon: Brain, name: 'MATRIZ Reasoning API', path: '/v1/reasoning', status: 'operational', latency: '~140ms', badge: 'stable' },
  { icon: Shield, name: 'Guardian Ethics API', path: '/v1/guardian', status: 'operational', latency: '~35ms', badge: 'stable' },
  { icon: Eye, name: 'Identity (ΛiD) API', path: '/v1/identity', status: 'operational', latency: '~80ms', badge: 'stable' },
  { icon: BookOpen, name: 'Memory Fold API', path: '/v1/memory', status: 'operational', latency: '~60ms', badge: 'stable' },
  { icon: Moon, name: 'Dream Engine API', path: '/v1/dream', status: 'operational', latency: '~220ms', badge: 'beta' },
  { icon: Leaf, name: 'Bio Adaptation API', path: '/v1/bio', status: 'operational', latency: '~45ms', badge: 'beta' },
  { icon: Scale, name: 'Consciousness Bus', path: '/v1/bus', status: 'operational', latency: '~18ms', badge: 'stable' },
  { icon: Atom, name: 'Quantum Compute API', path: '/v1/quantum', status: 'operational', latency: '~380ms', badge: 'experimental' },
]

const INCIDENT_LOG = [
  {
    date: '2026-03-10',
    title: 'Dream Engine API — elevated latency during cold-start burst',
    status: 'Resolved',
    duration: '12 minutes',
    impact: 'Minor',
  },
  {
    date: '2026-02-28',
    title: 'Scheduled maintenance: Memory Fold cluster upgrade',
    status: 'Completed',
    duration: '45 minutes',
    impact: 'None',
  },
]

const BADGE_STYLES: Record<string, string> = {
  stable: 'bg-vision-teal/15 text-vision-teal border-vision-teal/30',
  beta: 'bg-lambda-gold/15 text-lambda-gold border-lambda-gold/30',
  experimental: 'bg-white/10 text-white/50 border-white/20',
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'operational') return <CheckCircle className="w-4 h-4 text-success-green" />
  if (status === 'degraded') return <AlertTriangle className="w-4 h-4 text-warning-amber" />
  if (status === 'outage') return <XCircle className="w-4 h-4 text-error-red" />
  return <Activity className="w-4 h-4 text-white/30" />
}

export default function StatusPage() {
  const allOperational = CONSTELLATION_APIS.every(a => a.status === 'operational')

  return (
    <div className="min-h-screen bg-consciousness-deep text-awareness-silver">
      <SiteHeader />

      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border mb-6 ${
            allOperational
              ? 'bg-success-green/10 border-success-green/30'
              : 'bg-warning-amber/10 border-warning-amber/30'
          }`}>
            <span className={`w-1.5 h-1.5 rounded-full animate-pulse ${allOperational ? 'bg-success-green' : 'bg-warning-amber'}`} />
            <span className={`text-xs tracking-[0.15em] uppercase ${allOperational ? 'text-success-green' : 'text-warning-amber'}`} style={bodyFont}>
              {allOperational ? 'All Systems Operational' : 'Partial Degradation'}
            </span>
          </div>

          <h1 className="text-5xl md:text-6xl tracking-[0.12em] mb-4 text-white" style={brandFont}>
            API <span className="text-vision-teal">Status</span>
          </h1>
          <p className="text-lg text-white/55 max-w-xl mx-auto" style={bodyFont}>
            Real-time status for all Constellation Framework APIs
          </p>
        </motion.div>
      </section>

      {/* Summary metrics */}
      <section className="py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-2xl border border-white/[0.09] bg-white/[0.03] p-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center"
          >
            <div>
              <div className="text-2xl text-vision-teal mb-1" style={brandFont}>8 / 8</div>
              <div className="text-xs text-white/40 tracking-wide uppercase" style={bodyFont}>APIs operational</div>
            </div>
            <div>
              <div className="text-2xl text-vision-teal mb-1" style={brandFont}>99.7%</div>
              <div className="text-xs text-white/40 tracking-wide uppercase" style={bodyFont}>Guardian cascade prevention</div>
            </div>
            <div>
              <div className="text-2xl text-vision-teal mb-1" style={brandFont}>&lt;250ms</div>
              <div className="text-xs text-white/40 tracking-wide uppercase" style={bodyFont}>Context Bus handoff</div>
            </div>
            <div>
              <div className="text-2xl text-vision-teal mb-1" style={brandFont}>0</div>
              <div className="text-xs text-white/40 tracking-wide uppercase" style={bodyFont}>Active incidents</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Constellation API status table */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl tracking-[0.1em] mb-6 text-white" style={brandFont}>
            Constellation <span className="text-vision-teal">APIs</span>
          </h2>
          <div className="rounded-2xl border border-white/[0.09] bg-white/[0.02] overflow-hidden">
            {CONSTELLATION_APIS.map(({ icon: Icon, name, path, status, latency, badge }, i) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: i * 0.04 }}
                className={`flex items-center gap-4 px-6 py-4 hover:bg-white/[0.03] transition-colors ${i < CONSTELLATION_APIS.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
              >
                <div className="p-1.5 rounded-lg bg-vision-teal/10 shrink-0">
                  <Icon className="w-4 h-4 text-vision-teal" strokeWidth={1.5} />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-white" style={{ fontFamily: '"Helvetica Neue", sans-serif', fontWeight: 400 }}>
                      {name}
                    </span>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full border tracking-[0.08em] uppercase ${BADGE_STYLES[badge]}`}
                      style={bodyFont}
                    >
                      {badge}
                    </span>
                  </div>
                  <code className="text-[11px] text-vision-teal/60" style={{ fontFamily: '"JetBrains Mono", "Fira Code", monospace' }}>
                    {path}
                  </code>
                </div>

                <div className="flex items-center gap-6 shrink-0 text-right">
                  <div>
                    <div className="text-xs text-white/30 mb-0.5" style={bodyFont}>p95 latency</div>
                    <div className="text-sm text-code-cyan" style={{ fontFamily: '"JetBrains Mono", "Fira Code", monospace' }}>
                      {latency}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <StatusIcon status={status} />
                    <span className="text-xs text-success-green capitalize" style={bodyFont}>{status}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Incident log */}
      <section className="py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl tracking-[0.1em] mb-6 text-white" style={brandFont}>
            Recent <span className="text-vision-teal">Incidents</span>
          </h2>
          <div className="rounded-2xl border border-white/[0.09] bg-white/[0.02] overflow-hidden">
            {INCIDENT_LOG.map((incident, i) => (
              <div
                key={i}
                className={`px-6 py-5 ${i < INCIDENT_LOG.length - 1 ? 'border-b border-white/[0.06]' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-xs text-white/35" style={bodyFont}>{incident.date}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full border ${
                        incident.status === 'Resolved' || incident.status === 'Completed'
                          ? 'bg-success-green/10 text-success-green border-success-green/30'
                          : 'bg-warning-amber/10 text-warning-amber border-warning-amber/30'
                      }`} style={bodyFont}>
                        {incident.status}
                      </span>
                    </div>
                    <p className="text-sm text-white/75 mb-1" style={bodyFont}>{incident.title}</p>
                    <div className="flex gap-4 text-xs text-white/35" style={bodyFont}>
                      <span>Duration: {incident.duration}</span>
                      <span>Impact: {incident.impact}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-white/25 mt-3 text-center" style={bodyFont}>
            Full incident history at{' '}
            <a href="https://status.lukhas.io" className="text-vision-teal/60 hover:text-vision-teal transition-colors">
              status.lukhas.io
            </a>
          </p>
        </div>
      </section>

      {/* Subscribe */}
      <section className="py-24 px-6">
        <div className="max-w-xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-2xl border border-vision-teal/20 bg-vision-teal/5 p-10"
          >
            <h2 className="text-2xl tracking-[0.1em] mb-3 text-white" style={brandFont}>
              Subscribe to status updates
            </h2>
            <p className="text-sm text-white/50 mb-6" style={bodyFont}>
              Get notified for incidents and scheduled maintenance.
            </p>
            <div className="flex gap-3">
              <input
                type="email"
                placeholder="you@company.com"
                className="flex-1 px-4 py-2.5 rounded-full bg-white/[0.06] border border-white/[0.12] text-sm text-white placeholder-white/25 focus:outline-none focus:border-vision-teal/50 transition-colors"
                style={bodyFont}
              />
              <button
                className="px-5 py-2.5 rounded-full bg-vision-gradient text-white text-sm tracking-[0.06em] hover:opacity-90 transition-opacity shrink-0"
                style={bodyFont}
              >
                Subscribe
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}
